<template>
  <div class="live-auction-action-buttons" v-if="lots.length > 0">
    <div class="switch-block-bid-button-mob">
      <label class="switch">
        <input v-model="auctionStore.isLockedButtons" type="checkbox" />
        <span class="slider round"></span>
        <base-icon icon-name="lock" :class="{ show: !auctionStore.isLockedButtons }" view-box="0 0 17.721 23.259">
          <lock-icon></lock-icon>
        </base-icon>
        <base-icon icon-name="unlock" :class="{ show: auctionStore.isLockedButtons }" view-box="0 0 18 24">
          <unlock-icon></unlock-icon>
        </base-icon>
      </label>
      <span class="locked-button-label">{{ auctionStore.isLockedButtons ? "unlocked" : "locked" }}</span>
    </div>
    <template v-if="auctionStatus === AuctionStatuses.LIVE && lots.length > endedLots.length">
      <template v-if="activeLot?.id">
        <button class="dashboard-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob" @click="showDashboardPopup">
          <base-icon class="dashboard" icon-name="dashboard" width="20" height="20">
            <dashboard-icon></dashboard-icon>
          </base-icon>
        </button>
        <template v-if="auctionStore.isLockedButtons">
          <button v-if="!isScreenL" class="btn btn-lock-bidding locked btn-blue-outline" @click="toggleLockButtons">
            {{ trans("live.common.buttons.unlock_buttons") }}
            <base-icon icon-name="lock" view-box="0 0 17.721 23.259">
              <lock-icon></lock-icon>
            </base-icon>
          </button>
          <div v-else class="label-locked-btn btn-lock-bidding">
            {{ trans("live.bidding.bidding_locked") }}
          </div>
        </template>
        <template v-else>
          <button
            v-if="isLotCompleted"
            class="pass-lot-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob"
            @click="reopenLot">
            {{
              reopenLotState === 'reopen'
                ? trans('live.action_buttons.reopen_lot_label')
                : trans('live.action_buttons.confirm_reopen_lot_label')
            }}
          </button>
          <button
              v-else
              class="pass-lot-button btn btn-rounded btn-blue-outline btn-click-effect can-toggle-on-mob"
              @click="passLot"
          >
            {{
              passLotState === 'pass'
                ? trans('live.action_buttons.pass_lot_label')
                : trans('live.action_buttons.confirm_pass_lot_label')
            }}
          </button>

          <button
            class="book-bid-button btn btn-rounded btn-blue btn-click-effect can-toggle-on-mob"
            @click="placeBookBid(bidVariants[0])"
          >
            <span class="first-line">Book Bid</span>
            <span class="bid-value">{{ bookBidVariantLabel }}</span>
          </button>

          <button
            :class="[
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading || liveSettings?.is_change_bidding_status_auto }
            ]"
            :disabled="liveLotBtnIsLoading || liveSettings?.is_change_bidding_status_auto"
            v-if="!currentStep"
            @click="handleOnceBtn"
          >
            <span class="text">
              {{ liveSettings?.once_label ?? trans("live.action_buttons.once_label") }}
            </span>
            <span
                v-if="liveSettings?.is_change_bidding_status_auto"
                class="progress-bar"
                :style="{ width: progressWidth + '%', backgroundColor: progressColor }"
            ></span>
          </button>
          <button
            :class="[
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading || liveSettings?.is_change_bidding_status_auto }
            ]"
            :disabled="liveLotBtnIsLoading || liveSettings?.is_change_bidding_status_auto"
            v-if="currentStep === BiddingSteps.ONCE"
            @click="handleTwiceBtn"
          >
            <span class="text">
              {{ liveSettings?.twice_label ?? trans("live.action_buttons.twice_label") }}
            </span>
            <span
                v-if="liveSettings?.is_change_bidding_status_auto"
                class="progress-bar"
                :style="{ width: progressWidth + '%', backgroundColor: progressColor }"
            ></span>
          </button>
          <button
            :class="[
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading || liveSettings?.is_complete_current_lot_auto }
            ]"
            :disabled="liveLotBtnIsLoading || liveSettings?.is_complete_current_lot_auto"
            v-if="currentStep === BiddingSteps.TWICE"
            @click="handleCompleteLot"
          >
            <span class="text">
              {{
                activeLot.bids_count > 0
                  ? trans("live.action_buttons.sold_label")
                  : trans("live.action_buttons.unsold_label")
              }}
            </span>
            <span
              v-if="liveSettings?.is_complete_current_lot_auto"
              class="progress-bar"
              :style="{ width: progressWidth + '%', backgroundColor: progressColor }"
            ></span>
          </button>
          <button
            :class="[
              'live-lot-button btn btn-rounded btn-blue-outline btn-click-effect',
              { disabled: liveLotBtnIsLoading || liveSettings?.is_open_next_lot_auto }
            ]"
            :disabled="liveLotBtnIsLoading || liveSettings?.is_open_next_lot_auto"
            v-if="currentStep === BiddingSteps.COMPLETE_LOT"
            @click="handleNextLotBtn"
          >
            <span class="text">
              {{ nextLotLabel }}
            </span>
            <span
                v-if="liveSettings?.is_open_next_lot_auto"
                class="progress-bar"
                :style="{ width: progressWidth + '%', backgroundColor: progressColor }"
            ></span>
          </button>
        </template>
      </template>

      <template v-else-if="!isAuctionStarted">
        <button
          :class="[
            'start-auction-button btn btn-rounded btn-blue-outline btn-click-effect',
             { disabled: liveSettings?.is_open_next_lot_auto }
          ]"
          @click="startAuction"
        >
          {{ trans("live.action_buttons.start_auction") }}
        </button>
      </template>

      <template v-else>
        <button class="start-auction-button btn btn-rounded btn-blue-outline btn-click-effect" @click="continueAuction">
          {{ trans("live.action_buttons.continue_auction") }}
        </button>
      </template>
    </template>

    <template v-else-if="lots.length === endedLots.length">
      <button 
        class="start-auction-button btn btn-rounded btn-blue-outline btn-click-effect"
        :class="{ disabled: auction.status === AuctionStatuses.ENDED }"
        @click="endAuction"
      >
        {{
          auction.status === AuctionStatuses.ENDED
            ? trans("live.action_buttons.auction_completed")
            : trans("live.action_buttons.end_auction")
        }}
      </button>
    </template>
  </div>
  <popup class="dashboard-popup" title="Shortcuts" ref="popup"></popup>
</template>

<script setup lang="ts">
  import { computed, watch, ref, onMounted, markRaw, inject, reactive } from "vue";
  import Api from "@/services/api";
  import { useToast } from "primevue/usetoast";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { format_currency } from "@/utils/currencies";
  import { trans } from "@/utils/trans";
  import { IPopupError } from "@/models";
  import { AuctionStatuses, LotStatuses, BiddingSteps, LotSoldStatuses } from "@/enum";
  import { BaseIcon, DashboardIcon, LockIcon, UnlockIcon } from "@/components/icons";
  import Popup from "@/components/_partials/Popup.vue";
  import DashboardPopup from "@/components/_partials/DashboardPopup.vue";
  import { isScreenL } from "@/utils/media-size";

  const toast = useToast();
  const popup = ref<typeof Popup>();
  const component = markRaw(DashboardPopup);
  const emitter = inject("emitter");

  const auctionStore = useAuctionStore();
  const auction = computed(() => auctionStore.auction);
  const lots = computed(() => auctionStore.lots);
  const preparedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.PREPARED));
  const liveLot = computed(() => lots.value.find((item) => item.status === LotStatuses.LIVE));
  const endedLots = computed(() => lots.value.filter((item) => item.status === LotStatuses.ENDED));
  const isAuctionStarted = computed(() => liveLot.value?.id || endedLots.value.length);
  const auctionStatus = computed(() => auctionStore.auction.status);

  const activeLot = computed(() => auctionStore.activeLot);
  const currentBid = computed(() => auctionStore.activeLot?.current_bid);
  const bidsCount = computed(() => auctionStore.activeLot?.bids_count);
  const bookBid = computed(() => auctionStore.activeLot?.book_bid);
  const currentStep = computed(() => (auctionStore.activeLot?.id ? auctionStore.activeLot.live_step : 0));
  const liveSettings = computed(() => auctionStore.liveSettings);

  const isLotCompleted = computed(
      () =>
          activeLot.value?.sold_status === LotSoldStatuses.SOLD || activeLot.value?.sold_status === LotSoldStatuses.UNSOLD
  );

  const bidVariants = ref(auction.value.bid_variants);
  const bookBidVariantLabel = computed(() => format_currency(bidVariants.value[0]));
  const nextLotLabel = computed(() => {
    if (preparedLots.value.length === 0 && liveLot) {
      return trans("live.action_buttons.close_last_lot_label");
    } else if (auctionStore.liveSettings?.next_lot_label) {
      return auctionStore.liveSettings.next_lot_label;
    } else {
      return trans("live.action_buttons.next_lot_label");
    }
  });

  const liveLotBtnIsLoading = ref(false);

  let interval: number | undefined;
  const COLOR_NONE = "none";
  const COLOR_LIME = "#E0FFF7";
  const COLOR_GREY = "#F7F8FA";
  const remainingTime = ref(0);
  const progressWidth = ref(0);
  const progressColor = ref<string>(COLOR_NONE);

  const waitServer = reactive({
    retractBid: false
  });

  const passLotState = ref<'pass' | 'confirm'>('pass');
  const reopenLotState = ref<'reopen' | 'confirm'>('reopen');

  watch(currentBid, () => {
    refreshBidVariants();
  });

  watch(bidsCount, () => {
    refreshBidVariants();
  });

  watch(bookBid, () => {
    refreshBidVariants();
  });

  watch(
      () => auctionStore.refreshStepButtonTrigger,
      () => {
        fillStepButtonCheck();
      }
  );

  watch(() => auctionStore.liveSettings, () => {
    fillStepButtonCheck();
  }, { deep: true });

  onMounted(() => {
    refreshBidVariants();
    emitterSubscribe();
  });

  const passLot = () => {
    if (passLotState.value === 'pass') {
      passLotState.value = 'confirm';
    } else {
      Api.post("/live/lots/" + activeLot.value?.id + "/pass").catch((error) => {
        console.log(error);
      });

      passLotState.value = 'pass';
    }
  };

  const reopenLot = async () => {
    if (reopenLotState.value === 'reopen') {
      reopenLotState.value = 'confirm';
    } else {
      if (liveLot.value) {
        await auctionStore
          .reopenLot(liveLot.value)
          .catch((error) => {
            console.log(error);
          });
      } else {
        console.log('Live lot is not set!');
      }

      reopenLotState.value = 'reopen';
    }
  };

  const placeBookBid = (amount: number) => {
    if (activeLot.value?.id) {
      Api.post("/live/lots/" + activeLot.value?.id + "/place-bid", {
        amount: amount,
        is_book_bid: true
      })
        .then((response) => {
          if (response.data.success) {
            auctionStore.setActiveLotBookBid(amount);
          }
        })
        .catch((error) => {
          console.log(error);

          const errors = error.response.data.errors;
          Object.keys(errors).forEach((key) => {
            errors[key].forEach((error: IPopupError) => {
              toast.add({
                severity: "error",
                summary: error.title,
                detail: error.text,
                life: 6000
              });
            });
          });
        });
    }
  };

  const handleOnceBtn = async () => {
    if (liveSettings.value?.is_change_bidding_status_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-changing of status is enabled",
        life: 6000
      });
    } else {
      await runBiddingStep(BiddingSteps.ONCE);
    }
  };

  const handleTwiceBtn = async () => {
    if (liveSettings.value?.is_change_bidding_status_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-changing of status is enabled",
        life: 6000
      });
    } else {
      await runBiddingStep(BiddingSteps.TWICE);
    }
  };

  const handleCompleteLot = async () => {
    if (liveSettings.value?.is_complete_current_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Lot auto-closing is enabled",
        life: 6000
      });
    } else {
      await completeLot();
    }
  };

  const handleNextLotBtn = async () => {
    if (liveSettings.value?.is_open_next_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Auto-opening of the next lot is enabled",
        life: 6000
      });
    } else {
      await nextLot();
    }
  };

  const runBiddingStep = async (step: BiddingSteps) => {
    liveLotBtnIsLoading.value = true;
    await Api.post("/live/lots/" + activeLot.value?.id + "/bidding-step", { step })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        liveLotBtnIsLoading.value = false;
      });
  };

  const showDashboardPopup = () => {
    popup.value?.toggleComponent(component);
    popup.value?.open();
  };

  const toggleLockButtons = () => {
    auctionStore.$patch({
      isLockedButtons: !auctionStore.isLockedButtons
    });
  };

  const completeLot = async () => {
    liveLotBtnIsLoading.value = true;
    await Api.post("/live/lots/" + activeLot.value?.id + "/complete")
      .catch((error) => {
        toast.add({
          severity: "error",
          summary: error.response.data.message.title,
          detail: error.response.data.message.text,
          life: 6000
        });
      })
      .finally(() => {
        liveLotBtnIsLoading.value = false;
      });
  };

  const nextLot = async () => {
    liveLotBtnIsLoading.value = true;
    const nextLot = lots.value.find((item) => item.status === LotStatuses.PREPARED);
    if (nextLot) {
      return await auctionStore.activateLot(nextLot)
        .catch((error) => console.log(error))
        .finally(() => liveLotBtnIsLoading.value = false);
    } else if (liveLot.value) {
      return await auctionStore.closeLot(liveLot.value)
        .catch((error) => console.log(error))
        .finally(() => liveLotBtnIsLoading.value = false);
    }
  };

  const startAuction = async () => {
    if (liveSettings.value?.is_open_next_lot_auto) {
      toast.add({
        severity: "warn",
        summary: "Operation is blocked",
        detail: "Auto-opening of the next lot is enabled",
        life: 6000
      });
    } else {
      await auctionStore.startAuction(auction.value).catch((error) => {
        console.log(error);
      });
    }
  };

  const continueAuction = async () => {
    await nextLot();
  };

  const endAuction = () => {
    Api.post("/live/auctions/" + auction.value.id + "/complete")
      .then((response) => {
        toast.add({
          severity: response.data.success ? "success" : "error",
          summary: response.data.message.title,
          detail: response.data.message.text,
          life: 6000
        });
      })
      .catch((error) => {
        console.log(error);

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          errors[key].forEach((error: IPopupError) => {
            toast.add({
              severity: "error",
              summary: error.title,
              detail: error.text,
              life: 6000
            });
          });
        });
      });
  };

  const fillStepButtonCheck = () => {
    progressColor.value = COLOR_NONE;

    if (
      liveSettings.value?.is_change_bidding_status_auto &&
      [BiddingSteps.NONE, BiddingSteps.ONCE].includes(currentStep.value)
    ) {
      progressColor.value = COLOR_GREY;
      let delay = liveSettings.value?.change_bidding_status_mode || 0;
      fillStepButton(delay);
    }

    if (
      liveSettings.value?.is_complete_current_lot_auto &&
      currentStep.value === BiddingSteps.TWICE
    ) {
      progressColor.value = activeLot.value?.bids_count ? COLOR_LIME : COLOR_GREY;
      let delay = liveSettings.value?.complete_current_lot_mode || 0;
      fillStepButton(delay);
    }

    if (
      liveSettings.value?.is_open_next_lot_auto &&
      currentStep.value === BiddingSteps.COMPLETE_LOT
    ) {
      progressColor.value = COLOR_GREY;
      const delay = liveSettings.value?.open_next_lot_auto_mode || 0;
      fillStepButton(delay);
    }
  };

  const fillStepButton = (duration: number) => {
    if (interval) {
      clearInterval(interval);
      interval = undefined;
    }

    remainingTime.value = duration;
    progressWidth.value = 0;

    const startTime = Date.now();
    interval = setInterval(() => {
      const elapsedTime = (Date.now() - startTime) / 1000;
      remainingTime.value = Math.max(duration - Math.floor(elapsedTime), 0);
      progressWidth.value = Math.max((elapsedTime / duration) * 100, 0);

      if (elapsedTime >= duration) {
        clearInterval(interval);
      }
    }, 100);
  };

  const refreshBidVariants = () => {
    bidVariants.value = auctionStore.filteredBidVariants();
  };

  const retractBid = () => {
    if (waitServer.retractBid) return;

    waitServer.retractBid = true;
    Api.post("/live/lots/" + activeLot.value?.id + "/retract-bid")
      .then((response) => {
        if (response.data.success) {
          console.log("response.data", response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        waitServer.retractBid = false;
      });
  };

  const emitterSubscribe = () => {
    emitter?.on("hot-key:once-twice-sold", async () => {
      if (!currentStep.value) {
        await handleOnceBtn();
      } else if (currentStep.value === BiddingSteps.ONCE) {
        await handleTwiceBtn();
      } else if (currentStep.value === BiddingSteps.TWICE) {
        await handleCompleteLot();
      }
    });

    emitter?.on("hot-key:book-bid", () => placeBookBid(bidVariants.value[0]));
    emitter?.on("hot-key:book-bid-first", () => null); // todo Igor call book-bid-first
    emitter?.on("hot-key:pass-lot", () => passLot());
    emitter?.on("hot-key:retract-bid", () => retractBid());
    emitter?.on("shortcuts:retract-bid", () => retractBid());
  };
</script>

<style scoped>
  .live-lot-button {
    position: relative;
    overflow: hidden;
    z-index: 1;
  }

  .live-lot-button .text {
    z-index: 2;
  }

  .progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 0;
    transition: width 0.05s linear;
  }

  .live-lot-button .progress-bar {
    pointer-events: none;
  }
</style>
